import React, { useState } from "react";
import { UploadCloud, Globe, RefreshCw, Sparkles } from "lucide-react";
import { Modal } from "./Modal";
import * as api from "@/src/api/sdk.gen";
import { getErrorMessage } from "../../types";

export interface OrgDocumentUploadModalProps {
  isOpen: boolean;
  onClose: () => void;
  slug: string;
  onSuccess: () => Promise<void>;
}

export const OrgDocumentUploadModal: React.FC<OrgDocumentUploadModalProps> = ({
  isOpen,
  onClose,
  slug,
  onSuccess,
}) => {
  const [docUploadMode, setDocUploadMode] = useState<"file" | "link">("file");
  const [selectedDocFile, setSelectedDocFile] = useState<File | null>(null);
  const [newDocData, setNewDocData] = useState({
    title: "",
    document_type: "SOP",
    file_url: "",
    description: "",
  });
  const [uploadingDoc, setUploadingDoc] = useState(false);

  const resetForm = () => {
    setSelectedDocFile(null);
    setNewDocData({ title: "", document_type: "SOP", file_url: "", description: "" });
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!slug) return;
    try {
      setUploadingDoc(true);

      let isSuccess = false;
      let errorMessage: string | null = null;

      if (docUploadMode === "file") {
        if (!selectedDocFile) {
          alert("Please choose a file to upload (.pdf, .docx, .txt, .md).");
          setUploadingDoc(false);
          return;
        }
        const { data, error } = await api.uploadOrgDocumentApiV1B2bSlugDocumentsUploadPost({
          path: { slug },
          body: {
            file: selectedDocFile,
            title: newDocData.title.trim() || undefined,
            document_type: newDocData.document_type,
            description: newDocData.description.trim() || undefined,
          },
        });
        if (data) {
          isSuccess = true;
        } else {
          errorMessage = getErrorMessage(error, "Failed to upload document.");
        }
      } else {
        if (!newDocData.file_url.trim()) {
          alert("Please provide a valid document link/URL.");
          setUploadingDoc(false);
          return;
        }
        const { data, error } = await api.createOrgDocumentApiV1B2bSlugDocumentsPost({
          path: { slug },
          body: {
            title: newDocData.title,
            document_type: newDocData.document_type,
            file_url: newDocData.file_url,
            description: newDocData.description,
          },
        });
        if (data) {
          isSuccess = true;
        } else {
          errorMessage = getErrorMessage(error, "Failed to save document.");
        }
      }

      if (isSuccess) {
        await onSuccess();
        handleClose();
      } else if (errorMessage) {
        alert(errorMessage);
      }
    } catch (err) {
      console.error("Failed to add document:", err);
      alert("An unexpected error occurred while adding the document.");
    } finally {
      setUploadingDoc(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Add Organization Document / SOP"
      icon={<UploadCloud className="w-5 h-5 text-indigo-600" />}
      maxWidth="max-w-md"
    >
      {/* Mode Toggle */}
      <div className="flex p-1 bg-slate-100 rounded-xl mb-4 text-xs font-semibold">
        <button
          type="button"
          onClick={() => setDocUploadMode("file")}
          className={`flex-1 py-1.5 rounded-lg flex items-center justify-center gap-1.5 transition ${
            docUploadMode === "file"
              ? "bg-white text-indigo-700 shadow-xs font-bold"
              : "text-slate-600 hover:text-slate-900"
          }`}
        >
          <UploadCloud className="w-4 h-4" />
          Upload File
        </button>
        <button
          type="button"
          onClick={() => setDocUploadMode("link")}
          className={`flex-1 py-1.5 rounded-lg flex items-center justify-center gap-1.5 transition ${
            docUploadMode === "link"
              ? "bg-white text-indigo-700 shadow-xs font-bold"
              : "text-slate-600 hover:text-slate-900"
          }`}
        >
          <Globe className="w-4 h-4" />
          Web Link / URL
        </button>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4 text-xs">
        {docUploadMode === "file" ? (
          <div>
            <label className="block font-semibold text-slate-700 mb-1.5">
              Select Document (.pdf, .docx, .txt, .md) *
            </label>
            <label className="border-2 border-dashed border-slate-200 hover:border-indigo-400 rounded-2xl p-4 flex flex-col items-center justify-center gap-2 cursor-pointer bg-slate-50 hover:bg-indigo-50/40 transition">
              <input
                type="file"
                accept=".pdf,.docx,.txt,.md,.markdown"
                required={!selectedDocFile}
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) {
                    setSelectedDocFile(f);
                    if (!newDocData.title.trim()) {
                      const cleanName = f.name
                        .replace(/\.[^/.]+$/, "")
                        .replace(/[_-]+/g, " ")
                        .trim();
                      setNewDocData((prev) => ({ ...prev, title: cleanName }));
                    }
                  }
                }}
                className="hidden"
              />
              <UploadCloud className="w-8 h-8 text-indigo-500" />
              {selectedDocFile ? (
                <div className="text-center">
                  <p className="font-bold text-slate-800 text-xs">{selectedDocFile.name}</p>
                  <p className="text-[11px] text-slate-500">
                    {(selectedDocFile.size / 1024).toFixed(1)} KB · Click to choose different file
                  </p>
                </div>
              ) : (
                <div className="text-center">
                  <p className="font-semibold text-slate-700 text-xs">Click to browse or drag & drop</p>
                  <p className="text-[10px] text-slate-400 mt-0.5">
                    Supports PDF, Word (.docx), Markdown, or Plain Text
                  </p>
                </div>
              )}
            </label>
          </div>
        ) : (
          <div>
            <label className="block font-semibold text-slate-700 mb-1.5">Document / Web Link URL *</label>
            <input
              type="url"
              required
              placeholder="https://example.com/handbook.pdf or https://..."
              value={newDocData.file_url}
              onChange={(e) => setNewDocData((prev) => ({ ...prev, file_url: e.target.value }))}
              className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-slate-900 font-mono placeholder-slate-400 focus:bg-white focus:outline-none focus:border-indigo-500"
            />
            <p className="text-[10px] text-slate-400 mt-1">
              Our ingestion engine will fetch the page/file content and extract clean text automatically.
            </p>
          </div>
        )}

        <div>
          <label className="block font-semibold text-slate-700 mb-1.5">Document Title *</label>
          <input
            type="text"
            required
            placeholder="e.g. AI Engineering Guidelines 2026"
            value={newDocData.title}
            onChange={(e) => setNewDocData((prev) => ({ ...prev, title: e.target.value }))}
            className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-slate-900 placeholder-slate-400 focus:bg-white focus:outline-none focus:border-indigo-500"
          />
        </div>

        <div>
          <label className="block font-semibold text-slate-700 mb-1.5">Document Type</label>
          <select
            value={newDocData.document_type}
            onChange={(e) => setNewDocData((prev) => ({ ...prev, document_type: e.target.value }))}
            className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-slate-900 focus:bg-white focus:outline-none focus:border-indigo-500"
          >
            <option value="SOP">SOP (Standard Operating Procedure)</option>
            <option value="Policy">Company Policy</option>
            <option value="Manual">Training Manual</option>
            <option value="Guide">Engineering Guide</option>
            <option value="Case Study">Case Study</option>
          </select>
        </div>

        <div>
          <label className="block font-semibold text-slate-700 mb-1.5">Description (Optional)</label>
          <textarea
            rows={2}
            placeholder="Brief description of the document's purpose..."
            value={newDocData.description}
            onChange={(e) => setNewDocData((prev) => ({ ...prev, description: e.target.value }))}
            className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-slate-900 placeholder-slate-400 focus:bg-white focus:outline-none focus:border-indigo-500 resize-none"
          />
        </div>

        <div className="pt-4 border-t border-slate-100 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={handleClose}
            className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 font-semibold rounded-xl"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={uploadingDoc}
            className="px-5 py-2 bg-indigo-600 hover:bg-indigo-700 text-white font-bold rounded-xl shadow-sm flex items-center gap-1.5 disabled:opacity-60"
          >
            {uploadingDoc ? (
              <>
                <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                Processing & Vectorizing...
              </>
            ) : (
              <>
                <Sparkles className="w-3.5 h-3.5" />
                Save & Vectorize AI
              </>
            )}
          </button>
        </div>
      </form>
    </Modal>
  );
};
